import { supabase } from "@/integrations/supabase/client";
import { getTranslateLang } from "@/lib/translateUtils";

// Static menu item translations
const staticTranslations: Record<string, Record<string, string>> = {
  en: {
    "ראשי": "Home",
    "בלוג": "Blog",
    "אודות": "About",
    "שירות לקוחות": "Customer Service",
    "צור קשר": "Contact Us",
    "שאלות נפוצות": "FAQ",
    "מדריך מידות": "Size Guide",
    "מעקב אחרי תיקונים": "Track Repairs",
    "חזרה": "Back",
    "לצפייה בקולקציה": "View Collection",
  },
  ru: {
    "ראשי": "Главная",
    "בלוג": "Блог",
    "אודות": "О нас",
    "שירות לקוחות": "Обслуживание",
    "צור קשר": "Контакты",
    "שאלות נפוצות": "Частые вопросы",
    "מדריך מידות": "Гид размеров",
    "מעקב אחרי תיקונים": "Отслеживание ремонта",
    "חזרה": "Назад",
    "לצפייה בקולקציה": "Смотреть коллекцию",
  },
  ar: {
    "ראשי": "الرئيسية",
    "בלוג": "المدونة",
    "אודות": "من نحن",
    "שירות לקוחות": "خدمة العملاء",
    "צור קשר": "اتصل بنا",
    "שאלות נפוצות": "الأسئلة الشائعة",
    "מדריך מידות": "دليل المقاسات",
    "מעקב אחרי תיקונים": "تتبع الإصلاحات",
    "חזרה": "رجوع",
    "לצפייה בקולקציה": "عرض المجموعة",
  },
};

// Cache for dynamic translations loaded from DB
let dynamicTranslationsCache: Record<string, Record<string, string>> = {};
let cacheLoaded = false;

/**
 * Load dynamic translations from content_translations table
 * These are for category/collection names that come from Shopify
 */
export async function loadMenuTranslations(): Promise<void> {
  if (cacheLoaded) return;
  
  try {
    const { data, error } = await supabase
      .from("content_translations")
      .select("*")
      .eq("table_name", "menu")
      .in("language", ["en", "ru", "ar"]);

    if (error) throw error;

    const translations: Record<string, Record<string, string>> = { en: {}, ru: {}, ar: {} };
    
    if (data) {
      for (const row of data) {
        if (!translations[row.language]) translations[row.language] = {};
        // record_id stores the original Hebrew text, translated_text stores the translation
        translations[row.language][row.record_id] = row.translated_text;
      }
    }

    dynamicTranslationsCache = translations;
    cacheLoaded = true;
  } catch (error) {
    console.error("Error loading menu translations:", error);
  }
}

/**
 * Translate a menu item based on the current Google Translate language
 */
export function translateMenu(hebrewText: string): string {
  const lang = getTranslateLang();
  if (!lang) return hebrewText; // Hebrew - return as-is
  
  // Check static translations first
  const staticResult = staticTranslations[lang]?.[hebrewText];
  if (staticResult) return staticResult;
  
  // Check dynamic translations (from DB)
  const dynamicResult = dynamicTranslationsCache[lang]?.[hebrewText];
  if (dynamicResult) return dynamicResult;
  
  // Fallback to original
  return hebrewText;
}

/**
 * Get current menu language code
 */
export function getMenuLang(): string {
  return getTranslateLang() || "he";
}

/**
 * Invalidate cache (call when admin updates translations)
 */
export function invalidateMenuTranslationsCache(): void {
  cacheLoaded = false;
  dynamicTranslationsCache = {};
}
